import { ChangeEvent } from 'react'

import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core'

import {
  Header as GoalsTableHeader
} from 'components/GoalWizard/GoalsTable/styles'
import { StoreSelectionRow } from '../../domain/storeEligibility'
import { BulkGoalClasses, BulkGoalGroup, MonthOption } from '../../types'
import StoreSelectionCard from './StoreSelectionCard'

interface MonthStepProps {
  allAvailableSelected: boolean
  availableStoreIds: string[]
  classes: BulkGoalClasses
  groupedStores: Map<string, StoreSelectionRow[]>
  groups: BulkGoalGroup[]
  groupsLoading: boolean
  hasLeftStep0: boolean
  isCheckingAvailability: boolean
  monthOptions: MonthOption[]
  selectedMonth: string
  selectedStoreIds: Set<string>
  selectedStores: string[]
  storesError: string | null
  storesLoading: boolean
  storesWithoutGroup: StoreSelectionRow[]
  isGroupDisabled: (groupId: string) => boolean
  isGroupFullySelected: (groupId: string) => boolean
  isGroupPartiallySelected: (groupId: string) => boolean
  onMonthChange: (event: ChangeEvent<{ name?: string; value: unknown }>) => void
  toggleAllStores: () => void
  toggleGroupStores: (groupId: string) => void
  toggleStore: (storeId: string) => void
}

const MonthStep = ({
  allAvailableSelected,
  availableStoreIds,
  classes,
  groupedStores,
  groups,
  groupsLoading,
  hasLeftStep0,
  isCheckingAvailability,
  monthOptions,
  selectedMonth,
  selectedStoreIds,
  selectedStores,
  storesError,
  storesLoading,
  storesWithoutGroup,
  isGroupDisabled,
  isGroupFullySelected,
  isGroupPartiallySelected,
  onMonthChange,
  toggleAllStores,
  toggleGroupStores,
  toggleStore
}: MonthStepProps) => (
  <div className={classes.stepContainer}>
    <h1 className={classes.title}>A meta é para qual mês e quais lojas?</h1>
    <Typography className={classes.subtitle}>
      Escolha o mês e selecione todas as lojas que receberão a meta.
    </Typography>

    <FormControl className={classes.formControl} variant='outlined'>
      <InputLabel id='bulk-month-label'>
        {storesLoading ? <CircularProgress size={20} /> : 'Selecione um Mês'}
      </InputLabel>
      <Select labelId='bulk-month-label' label='Selecione um Mês' value={selectedMonth} onChange={onMonthChange} disabled={hasLeftStep0 || storesLoading}>
        {monthOptions.map(month => (
          <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
        ))}
      </Select>
    </FormControl>

    <div className={classes.guideBox}>
      <GoalsTableHeader>
        <div className='table-header-info'>
          <h1>Lojas do usuário</h1>
        </div>
      </GoalsTableHeader>
      {storesError && (
        <Typography color='error' variant='body2'>
          {storesError}
        </Typography>
      )}
      {(storesLoading || groupsLoading || isCheckingAvailability) && (
        <Box mt={2} display='flex' alignItems='center'>
          <CircularProgress size={18} style={{ marginRight: 8 }} />
          <Typography color='textSecondary' variant='body2'>
            {storesLoading || groupsLoading ? 'Carregando lojas...' : 'Validando disponibilidade das metas...'}
          </Typography>
        </Box>
      )}
      {!storesLoading && !groupsLoading && (
        <>
          <div className={classes.selectAllBar}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allAvailableSelected}
                  color='primary'
                  disabled={hasLeftStep0 || isCheckingAvailability || availableStoreIds.length === 0}
                  indeterminate={selectedStores.length > 0 && !allAvailableSelected}
                  onChange={toggleAllStores}
                />
              }
              label={<Typography variant='body2'>Selecionar todas as lojas disponíveis</Typography>}
            />
          </div>
          {groups.map(group => {
            const groupStoresList = groupedStores.get(group._id) || []
            if (groupStoresList.length === 0) return null
            const groupDisabled = isGroupDisabled(group._id)
            return (
              <div key={group._id} className={classes.groupSection}>
                <div className={classes.groupHeader}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isGroupFullySelected(group._id)}
                        color='primary'
                        disabled={hasLeftStep0 || isCheckingAvailability || groupDisabled}
                        indeterminate={isGroupPartiallySelected(group._id)}
                        onChange={() => toggleGroupStores(group._id)}
                      />
                    }
                    label={<Typography variant='body2' className={classes.groupName}>{group.name}</Typography>}
                  />
                </div>
                <div className={classes.storeList}>
                  {groupStoresList.map(row => (
                    <StoreSelectionCard classes={classes} disabled={isCheckingAvailability} hasLeftStep0={hasLeftStep0} key={row.store._id} row={row} selectedStoreIds={selectedStoreIds} toggleStore={toggleStore} />
                  ))}
                </div>
              </div>
            )
          })}
          {storesWithoutGroup.length > 0 && (
            <div className={classes.groupSection}>
              <div className={classes.groupHeader}>
                <Typography variant='body2' className={classes.groupName}>Lojas sem regional</Typography>
              </div>
              <div className={classes.storeList}>
                {storesWithoutGroup.map(row => (
                  <StoreSelectionCard classes={classes} disabled={isCheckingAvailability} hasLeftStep0={hasLeftStep0} key={row.store._id} row={row} selectedStoreIds={selectedStoreIds} toggleStore={toggleStore} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
)

export default MonthStep
